import { Reservation, RecentZoneResponse, ReservationContractSignupSaveRequest, Task, OpenTicketResponse } from '@parkingbase/ecommerce-api-demo';
import { ReservationService } from '@/services/reservation.service';

import { create } from 'zustand';
import { downloadBlob } from '@/lib/file-download';
import { logger } from '@/lib/logger';
import { extractApiErrorMessage } from '@/lib/api-error-utils';

const reservationService = new ReservationService();

interface ReservationState {
  currentReservations: Reservation[];
  pastReservations: Reservation[];
  recentZones: RecentZoneResponse[];
  pendingTasks: Task[];
  openTicketResponse: OpenTicketResponse | null;
  isLoading: boolean;
  /** Backend error from the most recent signContract failure, or null (PB-1712, additive). */
  error: string | null;
  clearError: () => void;
  getAllReservationsByUser: () => Promise<void>;
  reset: () => void;
  fetchRecentZones: () => Promise<void>;
  fetchOpenTicketDetails: () => Promise<void>;
  getAllPendingDisclaimertasks: () => Promise<void>;
  cancelReservation: (reservationId: string, wholeGroup?: boolean) => Promise<boolean>;
  downloadReceipt: (company: string, reservationId: string) => Promise<void>;
  signContract: (
    companyId: string,
    taskId: string,
    payload: ReservationContractSignupSaveRequest
  ) => Promise<boolean>;
}

export const useReservationStore = create<ReservationState>((set, get) => ({
  currentReservations: [],
  pastReservations: [],
  recentZones: [],
  pendingTasks: [],
  openTicketResponse: null,
  isLoading: false,
  error: null,
  clearError: () => set({ error: null }),
  reset: () =>
    set({
      currentReservations: [],
      pastReservations: [],
      recentZones: [],
      pendingTasks: [],
      openTicketResponse: null,
      isLoading: false,
    }),
  getAllReservationsByUser: async () => {
    try {
      set({ isLoading: true });
      const reservations = await reservationService.getReservationsByUser();
      set({
        pastReservations: reservations.pastReservation || [],
        currentReservations: reservations.currentReservation || [],
      });
    } catch (error) {
      logger.error('Failed to fetch reservations for user', error, {
        feature: 'reservation-store',
        route: 'getAllReservationsByUser',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchOpenTicketDetails: async () => {
    try {
      const data = await reservationService.getOpenTicketDetailsFromWhiteLabel();
      set({ openTicketResponse: data || null });
    } catch (error) {
      logger.error('Failed to fetch open ticket details', error, {
        feature: 'reservation-store',
        route: 'fetchOpenTicketDetails',
      });
      set({ openTicketResponse: null });
    }
  },
  fetchRecentZones: async () => {
    try {
      const data = await reservationService.getRecentZoneTickets();
      set({ recentZones: data || [] });
    } catch (error) {
      logger.error('Failed to fetch recent zone tickets', error, {
        feature: 'reservation-store',
        route: 'fetchRecentZones',
      });
    }
  },
  getAllPendingDisclaimertasks: async () => {
    try {
      set({ isLoading: true });
      const tasks = await reservationService.getPendingDisclaimertasks();
      set({ pendingTasks: tasks });
    } catch (error) {
      logger.error('Failed to fetch pending disclaimer tasks', error, {
        feature: 'reservation-store',
        route: 'getAllPendingDisclaimertasks',
      });
    } finally {
      set({ isLoading: false });
    }
  },
  cancelReservation: async (reservationId: string, wholeGroup?: boolean) => {
    try {
      if (wholeGroup === undefined) {
        await reservationService.cancelReservation(reservationId);
      } else {
        await reservationService.cancelReservation(reservationId, wholeGroup);
      }
      await get().getAllReservationsByUser();
      return true;
    } catch (error) {
      logger.error('Failed to cancel reservation', error, {
        feature: 'reservation-store',
        route: 'cancelReservation',
      });
      return false;
    }
  },
  downloadReceipt: async (company: string, reservationId: string) => {
    try {
      const fileData = await reservationService.downloadReceipt(company, reservationId);
      downloadBlob(fileData, `receipt-${reservationId}.pdf`);
    } catch (error) {
      logger.error('Failed to download receipt', error, {
        feature: 'reservation-store',
        route: 'downloadReceipt',
      });
    }
  },
  signContract: async (
    companyId: string,
    taskId: string,
    payload: ReservationContractSignupSaveRequest
  ) => {
    set({ error: null });
    try {
      await reservationService.signContract(companyId, taskId, payload);
      await get().getAllPendingDisclaimertasks();
      return true;
    } catch (error) {
      logger.error('Failed to sign contract', error, {
        feature: 'reservation-store',
        route: 'signContract',
      });
      set({ error: extractApiErrorMessage(error, '') || null });
      return false;
    }
  },
}));
